import { createContext, useContext, useReducer, useCallback, useEffect, useRef, ReactNode } from 'react'
import { Dream, CreateDreamDto, UpdateDreamDto } from '../types/dream.types'
import { dreamityPlannerRepository } from '@/services/dreamityPlannerRepository'
import { pullDreamitySnapshot, scheduleDebouncedPushDreamitySnapshot } from '@/services/dreamitySyncService'

interface DreamState {
  dreams: Dream[]
  selectedDream: Dream | null
  loading: boolean
  error: string | null
}

type DreamAction =
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'SET_DREAMS'; payload: Dream[] }
  | { type: 'SET_SELECTED_DREAM'; payload: Dream | null }
  | { type: 'UPDATE_DREAM_IN_LIST'; payload: Dream }
  | { type: 'DELETE_DREAM'; payload: string }

interface DreamContextType extends DreamState {
  /** 로컬 스냅샷만 반영. 서버 동기화는 가시성(포그라운드/백그라운드) 또는 로그인 부트스트랩에서만 수행 */
  fetchDreams: () => Promise<void>
  fetchDreamById: (dreamId: string) => Promise<Dream | null>
  createDream: (dream: CreateDreamDto) => Promise<void>
  updateDream: (dreamId: string, dream: UpdateDreamDto) => Promise<void>
  deleteDream: (dreamId: string) => Promise<void>
  selectDream: (dream: Dream | null) => void
}

const DreamContext = createContext<DreamContextType | undefined>(undefined)

const initialState: DreamState = {
  dreams: [],
  selectedDream: null,
  loading: false,
  error: null,
}

function dreamReducer(state: DreamState, action: DreamAction): DreamState {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, loading: action.payload }
    case 'SET_ERROR':
      return { ...state, error: action.payload }
    case 'SET_DREAMS':
      return { ...state, dreams: action.payload }
    case 'SET_SELECTED_DREAM':
      return { ...state, selectedDream: action.payload }
    case 'UPDATE_DREAM_IN_LIST':
      return {
        ...state,
        dreams: state.dreams.map((dream) =>
          dream.dreamId === action.payload.dreamId ? action.payload : dream
        ),
        selectedDream:
          state.selectedDream?.dreamId === action.payload.dreamId
            ? action.payload
            : state.selectedDream,
      }
    case 'DELETE_DREAM':
      return {
        ...state,
        dreams: state.dreams.filter((dream) => dream.dreamId !== action.payload),
        selectedDream:
          state.selectedDream?.dreamId === action.payload ? null : state.selectedDream,
      }
    default:
      return state
  }
}

interface DreamProviderProps {
  children: ReactNode
}

export function DreamProvider({ children }: DreamProviderProps) {
  const [state, dispatch] = useReducer(dreamReducer, initialState)
  const hasInitialized = useRef(false)
  const lastTokenRef = useRef<string | null>(null)

  const loadDreamsFromLocal = useCallback(() => {
    const token = localStorage.getItem('token')
    if (!token) return
    const dreams = dreamityPlannerRepository.listDreams()
    dispatch({ type: 'SET_DREAMS', payload: dreams })
  }, [])

  /** 로그인 직후·앱 최초 진입 등: 로컬 먼저 그린 뒤 서버 스냅샷과 한 번 맞춤 (메뉴 이동용 fetchDreams와 분리) */
  const bootstrapDreamsWithServerMerge = useCallback(async () => {
    const token = localStorage.getItem('token')
    if (!token) return

    dispatch({ type: 'SET_ERROR', payload: null })
    loadDreamsFromLocal()
    try {
      await pullDreamitySnapshot()
      dispatch({ type: 'SET_DREAMS', payload: dreamityPlannerRepository.listDreams() })
    } catch (error: any) {
      if (error?.response?.status === 401) return
      dispatch({ type: 'SET_ERROR', payload: (error as Error).message })
    }
  }, [loadDreamsFromLocal])

  const fetchDreams = useCallback(async () => {
    loadDreamsFromLocal()
  }, [loadDreamsFromLocal])

  const fetchDreamById = useCallback(async (dreamId: string): Promise<Dream | null> => {
    dispatch({ type: 'SET_ERROR', payload: null })
    try {
      const dream = dreamityPlannerRepository.getDream(dreamId)
      if (dream) {
        dispatch({ type: 'SET_SELECTED_DREAM', payload: dream })
        dispatch({ type: 'UPDATE_DREAM_IN_LIST', payload: dream })
      }
      return dream ?? null
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: (error as Error).message })
      throw error
    }
  }, [])

  const createDream = async (dream: CreateDreamDto) => {
    dispatch({ type: 'SET_LOADING', payload: true })
    dispatch({ type: 'SET_ERROR', payload: null })
    try {
      dreamityPlannerRepository.createDream(dream)
      dispatch({ type: 'SET_DREAMS', payload: dreamityPlannerRepository.listDreams() })
      scheduleDebouncedPushDreamitySnapshot()
      dispatch({ type: 'SET_LOADING', payload: false })
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: (error as Error).message })
      dispatch({ type: 'SET_LOADING', payload: false })
      throw error
    }
  }

  const updateDream = async (dreamId: string, dream: UpdateDreamDto) => {
    dispatch({ type: 'SET_LOADING', payload: true })
    dispatch({ type: 'SET_ERROR', payload: null })
    try {
      const updated = dreamityPlannerRepository.updateDream(dreamId, dream)
      if (updated) dispatch({ type: 'UPDATE_DREAM_IN_LIST', payload: updated })
      scheduleDebouncedPushDreamitySnapshot()
      dispatch({ type: 'SET_LOADING', payload: false })
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: (error as Error).message })
      dispatch({ type: 'SET_LOADING', payload: false })
      throw error
    }
  }

  const deleteDream = async (dreamId: string) => {
    dispatch({ type: 'SET_LOADING', payload: true })
    dispatch({ type: 'SET_ERROR', payload: null })
    try {
      dreamityPlannerRepository.deleteDream(dreamId)
      dispatch({ type: 'DELETE_DREAM', payload: dreamId })
      scheduleDebouncedPushDreamitySnapshot()
      dispatch({ type: 'SET_LOADING', payload: false })
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: (error as Error).message })
      dispatch({ type: 'SET_LOADING', payload: false })
      throw error
    }
  }

  const selectDream = (dream: Dream | null) => {
    dispatch({ type: 'SET_SELECTED_DREAM', payload: dream })
  }

  useEffect(() => {
    const currentPath = window.location.pathname
    if (
      currentPath === '/login' ||
      currentPath.startsWith('/login/') ||
      currentPath === '/auth/callback' ||
      currentPath.startsWith('/auth/callback')
    ) {
      return
    }

    const token = localStorage.getItem('token')
    if (!token) return
    if (hasInitialized.current) return

    hasInitialized.current = true
    bootstrapDreamsWithServerMerge().catch((error: any) => {
      if (error?.response?.status !== 401) {
        console.error('초기 꿈 목록 로드 실패:', error)
      }
      hasInitialized.current = false
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'token' && e.newValue && e.newValue !== lastTokenRef.current) {
        lastTokenRef.current = e.newValue
        if (!hasInitialized.current) {
          hasInitialized.current = true
          bootstrapDreamsWithServerMerge().catch(() => {
            hasInitialized.current = false
          })
        }
      } else if (e.key === 'token' && !e.newValue) {
        lastTokenRef.current = null
        hasInitialized.current = false
        dispatch({ type: 'SET_DREAMS', payload: [] })
      }
    }

    window.addEventListener('storage', handleStorageChange)

    const checkToken = () => {
      const token = localStorage.getItem('token')
      if (token && token !== lastTokenRef.current && !hasInitialized.current) {
        lastTokenRef.current = token
        hasInitialized.current = true
        bootstrapDreamsWithServerMerge().catch(() => {
          hasInitialized.current = false
        })
      }
    }

    const interval = setInterval(checkToken, 3000)

    return () => {
      window.removeEventListener('storage', handleStorageChange)
      clearInterval(interval)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    const handleLoginSuccess = () => {
      hasInitialized.current = true
      void bootstrapDreamsWithServerMerge().catch(() => {
        hasInitialized.current = false
      })
    }

    window.addEventListener('LOGIN_SUCCESS', handleLoginSuccess)
    return () => {
      window.removeEventListener('LOGIN_SUCCESS', handleLoginSuccess)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <DreamContext.Provider
      value={{
        ...state,
        fetchDreams,
        fetchDreamById,
        createDream,
        updateDream,
        deleteDream,
        selectDream,
      }}
    >
      {children}
    </DreamContext.Provider>
  )
}

export function useDreamContext() {
  const context = useContext(DreamContext)
  if (context === undefined) {
    throw new Error('useDream must be used within a DreamProvider')
  }
  return context
}
